// UDOMI-487: Work Order Traveler — RTK Query slice for task trackers and traveler data

import { createApi } from '@reduxjs/toolkit/query/react';
import { TaskTrackerRequest, TaskTrackerResponse, TravelerData } from '../../types';
import { baseQueryStub } from '../../utils';
import { TaskTrackerService } from './task-tracker-service';

// A helper function to initialize the service once when needed
let taskTrackerService: TaskTrackerService | null = null;
const getTaskTrackerService = () => {
  taskTrackerService ??= new TaskTrackerService();
  return taskTrackerService;
};

export const taskTrackersApi = createApi({
  reducerPath: 'taskTrackersApi',
  baseQuery: baseQueryStub,
  tagTypes: ['TaskTracker', 'TravelerData'],
  refetchOnMountOrArgChange: true,
  refetchOnReconnect: true,
  endpoints: builder => ({
    /**
     * Get the composite Work Order Traveler data for an order.
     * Single call that returns order, job details, buildings, trusses, task trackers, technicians.
     */
    getTravelerData: builder.query<TravelerData, number>({
      queryFn: async orderId => {
        const service = getTaskTrackerService();
        const data = await service.getTravelerData(orderId);
        return { data };
      },
      providesTags: (_result, _error, orderId) => [
        { type: 'TravelerData', id: orderId },
        { type: 'TaskTracker', id: 'LIST' },
      ],
      keepUnusedDataFor: 30,
    }),

    /**
     * Create a new task tracker stage (first save for a virtual/unhardcoded stage).
     * Returns the created tracker with its real DB id.
     */
    createTaskTracker: builder.mutation<
      TaskTrackerResponse,
      { orderId: number; request: TaskTrackerRequest }
    >({
      queryFn: async ({ request }) => {
        const service = getTaskTrackerService();
        const result = await service.createTaskTracker(request);
        return { data: result };
      },
      invalidatesTags: (_result, _error, { orderId }) => [
        { type: 'TravelerData', id: orderId },
        { type: 'TaskTracker', id: 'LIST' },
      ],
    }),

    /**
     * Update a task tracker stage (mark stage as Complete with technician + initials).
     * orderId must be included so the invalidation tag matches the providesTags key used
     * by getTravelerData, causing the traveler query to refetch after every stage save.
     */
    updateTaskTracker: builder.mutation<
      TaskTrackerResponse,
      { id: number; orderId: number; request: TaskTrackerRequest }
    >({
      queryFn: async ({ id, request }) => {
        const service = getTaskTrackerService();
        const result = await service.updateTaskTracker(id, request);
        return { data: result };
      },
      invalidatesTags: (_result, _error, { id, orderId }) => [
        { type: 'TaskTracker', id },
        { type: 'TravelerData', id: orderId },
      ],
    }),
  }),
});

export const {
  useGetTravelerDataQuery,
  useCreateTaskTrackerMutation,
  useUpdateTaskTrackerMutation,
} = taskTrackersApi;
